import { Node, Edge } from 'reactflow';
import { logNodePosition, validatePosition, logAllNodePositions } from '../../../utils/positionDebugUtils';

// Calculate critical path through the workflow
export function calculateCriticalPath(workflow: any): string[] {
  // Implementation of critical path calculation
  if (!workflow.phases || !workflow.connections) return [];

  // Get only phase IDs
  const phaseIds = workflow.phases.map(phase => phase.id);

  // Build adjacency list (only for phases)
  const graph: Record<string, string[]> = {};
  phaseIds.forEach(id => {
    graph[id] = [];
  });

  // Only consider connections between phases
  workflow.connections.forEach(conn => {
    if (phaseIds.includes(conn.source) && phaseIds.includes(conn.target)) {
      graph[conn.source].push(conn.target);
    }
  });

  // Calculate durations
  const durations: Record<string, number> = {};
  workflow.phases.forEach(phase => {
    durations[phase.id] = parseInt(phase.config?.duration || '0', 10) || 0;
  });

  // Find start nodes (no incoming edges)
  const inDegree: Record<string, number> = {};
  phaseIds.forEach(id => {
    inDegree[id] = 0;
  });

  workflow.connections.forEach(conn => {
    if (phaseIds.includes(conn.source) && phaseIds.includes(conn.target)) {
      inDegree[conn.target]++;
    }
  });

  const startNodes = phaseIds.filter(id => inDegree[id] === 0);

  // If no start nodes, return empty path
  if (startNodes.length === 0) return [];

  // Calculate earliest completion time
  const earliestCompletion: Record<string, number> = {};
  const queue: string[] = [...startNodes];

  startNodes.forEach(node => {
    earliestCompletion[node] = durations[node];
  });

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const neighbor of graph[current] || []) {
      earliestCompletion[neighbor] = Math.max(
        earliestCompletion[neighbor] || 0,
        earliestCompletion[current] + durations[neighbor]
      );

      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
      }
    }
  }

  // Find end nodes (no outgoing edges)
  const endNodes = phaseIds.filter(id => (graph[id] || []).length === 0);

  // If no end nodes, return empty path
  if (endNodes.length === 0) return [];

  // Find the node with the latest completion time
  let maxTime = 0;
  let maxNode = '';

  endNodes.forEach(node => {
    if (earliestCompletion[node] > maxTime) {
      maxTime = earliestCompletion[node];
      maxNode = node;
    }
  });

  // If no max node found, return empty path
  if (!maxNode) return [];

  // Backtrack to find the critical path
  const criticalPath = [maxNode];
  let currentNode = maxNode;

  while (currentNode && !startNodes.includes(currentNode)) {
    let prevNode = '';
    let prevTime = 0;

    for (const id of phaseIds) {
      if (graph[id] && graph[id].includes(currentNode)) {
        const time = earliestCompletion[id];
        if (time > prevTime) {
          prevTime = time;
          prevNode = id;
        }
      }
    }

    if (prevNode) {
      criticalPath.unshift(prevNode);
      currentNode = prevNode;
    } else {
      break;
    }
  }

  return criticalPath;
}

// Count dependencies for a phase
export function countDependencies(workflow: any, phaseId: string): number {
  if (!workflow.connections) return 0;

  // Count incoming and outgoing dependencies
  const incoming = workflow.connections.filter(conn => conn.target === phaseId).length;
  const outgoing = workflow.connections.filter(conn => conn.source === phaseId).length;

  return incoming + outgoing;
}

// Arrange nodes in a timeline layout
export function arrangeNodesInTimeline(nodes: Node[], workflow: any): Node[] {
  console.log("🔄 TIMELINE LAYOUT START");
  logAllNodePositions('TIMELINE_LAYOUT_START', nodes);
  
  if (!workflow.phases || !workflow.connections) return nodes;

  // Build adjacency list
  const graph: Record<string, string[]> = {};
  workflow.phases.forEach(phase => {
    graph[phase.id] = [];
  });

  workflow.connections.forEach(conn => {
    graph[conn.source].push(conn.target);
  });

  // Calculate levels (timeline positions)
  const levels: Record<string, number> = {};
  const visited = new Set<string>();
  const queue: [string, number][] = [];

  // Find start nodes (no incoming edges)
  const inDegree: Record<string, number> = {};
  workflow.phases.forEach(phase => {
    inDegree[phase.id] = 0;
  });

  workflow.connections.forEach(conn => {
    inDegree[conn.target]++;
  });

  const startNodes = workflow.phases
    .filter(phase => inDegree[phase.id] === 0)
    .map(phase => phase.id);

  // BFS to assign levels
  startNodes.forEach(node => {
    queue.push([node, 0]);
  });

  while (queue.length > 0) {
    const [node, level] = queue.shift()!;

    if (visited.has(node)) {
      levels[node] = Math.max(levels[node], level);
    } else {
      levels[node] = level;
      visited.add(node);
    }

    graph[node].forEach(neighbor => {
      queue.push([neighbor, level + 1]);
    });
  }

  // Arrange nodes based on levels
  const levelGroups: Record<number, string[]> = {};
  Object.entries(levels).forEach(([node, level]) => {
    if (!levelGroups[level]) {
      levelGroups[level] = [];
    }
    levelGroups[level].push(node);
  });

  // Position nodes
  const HORIZONTAL_SPACING = 250;
  const VERTICAL_SPACING = 150;

  const result = nodes.map(node => {
    const level = levels[node.id] || 0;
    const nodesInLevel = levelGroups[level] || [];
    const indexInLevel = nodesInLevel.indexOf(node.id);

    const newPosition = {
      x: level * HORIZONTAL_SPACING + 50,
      y: indexInLevel * VERTICAL_SPACING + 50
    };
    
    logNodePosition('TIMELINE_LAYOUT', node.id, {
      from: node.position,
      to: newPosition
    });
    
    return {
      ...node,
      position: newPosition
    };
  });
  
  logAllNodePositions('TIMELINE_LAYOUT_RESULT', result);
  console.log("🔄 TIMELINE LAYOUT COMPLETE");
  
  return result;
}

// Arrange nodes to highlight parallel execution opportunities
export function arrangeNodesForParallelism(nodes: Node[], workflow: any): Node[] {
  console.log("🔄 PARALLEL LAYOUT START");
  logAllNodePositions('PARALLEL_LAYOUT_START', nodes);
  
  if (!workflow.phases || !workflow.connections) return nodes;

  // Identify parallel paths
  const parallelSets = identifyParallelOpportunities(workflow);

  // Group nodes by parallel set
  const nodeGroups: Record<string, Set<string>> = {};

  parallelSets.forEach((set, index) => {
    nodeGroups[`parallel-${index}`] = new Set(set);
  });

  // Assign remaining nodes to a default group
  const assignedNodes = new Set<string>();
  Object.values(nodeGroups).forEach(group => {
    group.forEach(nodeId => {
      assignedNodes.add(nodeId);
    });
  });

  const unassignedNodes = workflow.phases
    .map(phase => phase.id)
    .filter(id => !assignedNodes.has(id));

  if (unassignedNodes.length > 0) {
    nodeGroups['default'] = new Set(unassignedNodes);
  }

  // Position nodes based on groups
  const HORIZONTAL_SPACING = 300;
  const VERTICAL_SPACING = 100;

  const result = nodes.map(node => {
    // Find which group this node belongs to
    let groupIndex = -1;
    let indexInGroup = 0;

    Object.entries(nodeGroups).forEach(([group, nodeIds], index) => {
      if (nodeIds.has(node.id)) {
        groupIndex = index;
        indexInGroup = Array.from(nodeIds).indexOf(node.id);
      }
    });

    const newPosition = {
      x: groupIndex * HORIZONTAL_SPACING + 50,
      y: indexInGroup * VERTICAL_SPACING + 50
    };
    
    logNodePosition('PARALLEL_LAYOUT', node.id, {
      from: node.position,
      to: newPosition
    });
    
    return {
      ...node,
      position: newPosition
    };
  });
  
  logAllNodePositions('PARALLEL_LAYOUT_RESULT', result);
  console.log("🔄 PARALLEL LAYOUT COMPLETE");
  
  return result;
}

// Identify parallel execution opportunities
export function identifyParallelOpportunities(workflow: any): string[][] {
  if (!workflow.phases || !workflow.connections) return [];

  // Build adjacency list
  const graph: Record<string, string[]> = {};
  workflow.phases.forEach(phase => {
    graph[phase.id] = [];
  });

  workflow.connections.forEach(conn => {
    graph[conn.source].push(conn.target);
  });

  // Find independent paths through the graph
  const visited = new Set<string>();
  const paths: string[][] = [];

  // Find start nodes (no incoming edges)
  const inDegree: Record<string, number> = {};
  workflow.phases.forEach(phase => {
    inDegree[phase.id] = 0;
  });

  workflow.connections.forEach(conn => {
    inDegree[conn.target]++;
  });

  const startNodes = workflow.phases
    .filter(phase => inDegree[phase.id] === 0)
    .map(phase => phase.id);

  // DFS from each start node
  startNodes.forEach(startNode => {
    const stack = [{ node: startNode, path: [startNode] }];

    while (stack.length > 0) {
      const { node, path } = stack.pop()!;

      if (graph[node].length === 0) {
        // End node reached
        paths.push(path);
      } else {
        graph[node].forEach(neighbor => {
          stack.push({
            node: neighbor,
            path: [...path, neighbor]
          });
        });
      }
    }
  });

  // Find parallel paths (paths that don't share nodes)
  const parallelSets: string[][] = [];

  for (let i = 0; i < paths.length; i++) {
    for (let j = i + 1; j < paths.length; j++) {
      // Check if paths share any nodes
      const pathSet1 = new Set(paths[i]);
      const pathSet2 = new Set(paths[j]);

      let hasCommon = false;
      for (const node of pathSet1) {
        if (pathSet2.has(node)) {
          hasCommon = true;
          break;
        }
      }

      if (!hasCommon) {
        parallelSets.push([...paths[i], ...paths[j]]);
      }
    }
  }

  return parallelSets;
}
// Apply layout based on view mode
export function applyLayout(
  nodes: Node[],
  setNodes: (nodes: Node[] | ((nds: Node[]) => Node[])) => void,
  viewMode: string,
  workflowData: any,
  fitView: (options?: any) => void
): void {
  console.log(`🔄 APPLY LAYOUT START - View Mode: ${viewMode}`);
  logAllNodePositions('APPLY_LAYOUT_START', nodes);
  
  if (!workflowData) return;

  // In default mode, NEVER apply automatic layout - always use positions from the data
  if (viewMode === 'default') {
    console.log("Default view mode: preserving original node positions");
    
    // Only fit view to ensure all nodes are visible
    setTimeout(() => fitView({ padding: 0.2 }), 100);
    return;
  }
  
  // For other view modes, apply the appropriate layout
  console.log(`Applying ${viewMode} layout to nodes`);
  
  switch (viewMode) {
    case 'timeline':
      // Arrange nodes in a timeline layout
      setNodes(nodes => arrangeNodesInTimeline(nodes, workflowData));
      break;
    case 'parallel':
      // Arrange nodes to highlight parallel execution opportunities
      setNodes(nodes => arrangeNodesForParallelism(nodes, workflowData));
      break;
  }

  // Log positions after layout is applied
  setTimeout(() => {
    console.log(`🔄 APPLY LAYOUT COMPLETE - View Mode: ${viewMode}`);
  }, 200);
  
  setTimeout(() => fitView({ padding: 0.2 }), 100);
}
